import React, { useEffect, useState } from 'react'
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, CircularProgress } from '@material-ui/core'
import PhotoCameraIcon from '@material-ui/icons/PhotoCamera'
import QRCode from 'qrcode'
import api from '../../api/client'
import { logoSrc, uploadLogo } from '../../api/logoSrc'

/**
 * پیشنهادِ ثبت لوگو/عکس برای کاربرانی که هنوز تصویری ندارند.
 *  - روی موبایل: دکمهٔ «گرفتن عکس با دوربین»
 *  - روی کامپیوتر: کیوآرکدی که با موبایل اسکن می‌شود ⇒ صفحهٔ عمومیِ ثبت تصویر
 */
export default function LogoPromptDialog({ open, onClose, userInfo, onUploaded }) {
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const [error, setError] = useState('')
  const [qr, setQr] = useState('')
  const [qrLoading, setQrLoading] = useState(false)
  const [mobile, setMobile] = useState(false)

  // تشخیصِ موبایل/تبلت
  useEffect(() => {
    if (!open) return
    const ua = navigator.userAgent || ''
    const isTouch = /Android|iPhone|iPad|iPod|Mobile|Windows Phone/i.test(ua)
    const narrow = window.innerWidth < 820
    setMobile(isTouch || narrow)
  }, [open])

  // ساختِ کیوآرکد برای دسکتاپ
  useEffect(() => {
    if (!open || mobile || !userInfo || !userInfo.id) return undefined
    let alive = true
    setQrLoading(true)
    api.get('users/logo-upload-token/')
      .then((res) => {
        if (!alive) return
        const token = res.data && res.data.token
        if (!token) throw new Error('no token')
        const url = `${window.location.origin}/logo-upload?token=${token}`
        return QRCode.toDataURL(url, {
          width: 220,
          margin: 1,
          color: { dark: '#101a4d', light: '#ffffff' },
        }).then((dataUrl) => {
          if (alive) setQr(dataUrl)
        })
      })
      .catch(() => {
        if (alive) setQr('')
      })
      .finally(() => {
        if (alive) setQrLoading(false)
      })
    return () => { alive = false }
  }, [open, mobile, userInfo])

  // اگر کاربر از موبایل تصویر را ثبت کرد ⇒ دیالوگ بسته شود
  useEffect(() => {
    if (!open || !userInfo || !userInfo.id) return undefined
    let alive = true
    const check = () => {
      api.get(logoSrc(`users/logo/${userInfo.id}/`))
        .then(() => {
          if (!alive) return
          setMsg('تصویر شما ثبت شد ✓')
          if (onUploaded) onUploaded()
          setTimeout(() => { if (alive && onClose) onClose(true) }, 900)
        })
        .catch(() => {})
    }
    const t = setInterval(check, 4000)
    return () => { alive = false; clearInterval(t) }
  }, [open, userInfo, onClose, onUploaded])

  const handleFile = async (e) => {
    const file = e.target.files && e.target.files[0]
    if (!file) return
    setBusy(true)
    setMsg('')
    setError('')
    try {
      const path = await uploadLogo(file)
      if (path && onUploaded) onUploaded(path)
      setMsg('تصویر با موفقیت ثبت شد ✓')
      setTimeout(() => { if (onClose) onClose(true) }, 900)
    } catch (err) {
      setError((err && err.response && err.response.data && err.response.data.detail) || 'خطا در ارسال تصویر')
    } finally {
      setBusy(false)
      e.target.value = ''
    }
  }

  return (
    <Dialog open={!!open} onClose={() => onClose && onClose(false)} maxWidth="xs" fullWidth>
      <DialogTitle style={{ fontWeight: 'bold' }}>
        تکمیل پروفایل — ثبت لوگو / عکس
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" style={{ lineHeight: 2.2, color: '#555' }}>
          {mobile
            ? 'برای اینکه شناسهٔ شما در سامانه واضح باشد، لطفاً عکس یا لوگوی خود (سازمان) را ثبت کنید.'
            : 'شما با کامپیوتر وارد شده‌اید. کیوآرکدِ زیر را با موبایل اسکن کنید تا با دوربین، عکس یا لوگوی خود را ثبت کنید.'}
        </Typography>

        {!mobile && (
          <Box mt={2} textAlign="center">
            {qrLoading && <CircularProgress size={26} />}
            {qr && (
              <img
                src={qr}
                alt="QR ثبت تصویر"
                style={{ width: 190, height: 190, borderRadius: 10, border: '1px solid #e3e6f2' }}
              />
            )}
            {!qr && !qrLoading && (
              <Typography variant="body2" style={{ color: '#c62828' }}>
                ساخت کیوآرکد ممکن نشد؛ از دکمهٔ زیر استفاده کنید.
              </Typography>
            )}
            {qr && (
              <Typography variant="body2" style={{ color: '#5a6b8c', marginTop: 6 }}>
                با موبایل اسکن کنید
              </Typography>
            )}
          </Box>
        )}

        <Box mt={2} textAlign="center">
          <Button
            component="label"
            variant="contained"
            color="primary"
            disabled={busy}
            startIcon={busy ? <CircularProgress size={18} /> : <PhotoCameraIcon />}
            style={{ fontWeight: 'bold' }}
          >
            {busy ? 'در حال ارسال...' : mobile ? 'گرفتن عکس با دوربین' : 'انتخاب فایل از کامپیوتر'}
            <input hidden type="file" accept="image/*" capture="environment" onChange={handleFile} />
          </Button>
          {!mobile && (
            <Typography variant="caption" style={{ display: 'block', marginTop: 8, color: '#7a80a3' }}>
              یا از دکمهٔ «کیوآرکد» بالا استفاده کنید
            </Typography>
          )}
        </Box>

        {msg && (
          <Typography variant="body2" style={{ marginTop: 14, color: '#2e7d32', fontWeight: 700 }}>
            {msg}
          </Typography>
        )}
        {error && (
          <Typography variant="body2" style={{ marginTop: 14, color: '#c62828', fontWeight: 700 }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={() => onClose && onClose(false)} color="default" style={{ fontWeight: 'bold' }}>
          بعداً
        </Button>
      </DialogActions>
    </Dialog>
  )
}
